import React from 'react';
import './badge.css';

/**
 * Background is applied via badge.css using --badge-color-background so that
 * the variable and its usage live in the same file (import './badge.css').
 * This avoids relying on Tailwind arbitrary values like bg-[var(--badge-color-background)] which
 * may not resolve when the design-system is consumed by MFEs.
 */

export type BadgeProps = {
  /**
   * An optional icon, e.g. `<RocketLaunchIcon />`
   */
  icon?: React.ReactNode;
  /**
   * The main label, displayed in semi-bold
   */
  label: React.ReactNode;
  /**
   * An optional secondary label, displayed in normal weight
   */
  secondaryLabel?: React.ReactNode;
  /**
   * An optional description, displayed on hover
   */
  description?: React.ReactNode;
  /**
   * onClick handler when the badge is clicked
   */
  onClick?: () => void;
  /**
   * If the badge is representation of a scale, this is the status category of
   * the badge.
   */
  status?: 'good' | 'warning' | 'bad' | 'info' | 'neutral';
  /**
   * Additional classes to apply to the component, e.g. `'absolute top-0 right-full'`
   */
  className?: string;
  /**
   * Ref for the root element (React 19 passes ref as a prop). Enables use as
   * Tooltip trigger, etc.
   */
  ref?: React.Ref<HTMLSpanElement>;
};

/**
 * A badge component with a primary and a secondary label, which expands
 * on hover to show a description.
 *
 * Example:
 * ```tsx
 *   <Badge
 *     description="You should take immediate action"
 *     icon={<ConfidenceIcon />}
 *     onClick={onClick}
 *     label="Severity"
 *     secondaryLabel="Very high"
 *   />
 * ```
 */
export const Badge = React.memo(function Badge({
  className,
  description,
  icon,
  label,
  onClick,
  ref,
  secondaryLabel,
  status,
}: BadgeProps) {
  return (
    <span
      ref={ref}
      className={
        'badge group' +
        ' relative inline-flex flex-row items-center' +
        ' min-h-6 gap-y-2 rounded-md px-2 py-1' +
        ' text-xs text-black dark:text-white' +
        (className ? ` ${className}` : '') +
        (onClick && !description ? ` hover:shadow-action` : '') + // The description group-hover interferes with box-shadow
        (onClick ? ' cursor-pointer' : '') +
        (status ? ` badge--${status}` : '')
      }
      data-testid="badge"
      onClick={onClick}
    >
      {icon && (
        <span className="badge--icon mr-1 inline-flex h-3.5 w-3.5 shrink-0 [&>svg]:h-full [&>svg]:w-full">
          {icon}
        </span>
      )}
      <span
        className="badge--label font-medium text-wrap"
        data-testid="badge-label"
      >
        {label}
      </span>
      {secondaryLabel && (
        <span
          className="badge--value ml-1 font-normal"
          data-testid="badge-secondary-label"
        >
          {secondaryLabel}
        </span>
      )}
      {description && (
        <span
          className={
            'badge--description' +
            ' absolute top-0 left-[calc(100%-0.5rem)] z-10 w-max' +
            ' rounded-md py-1 pr-2' +
            ' overflow-hidden text-nowrap overflow-ellipsis' +
            ' transition-all duration-600' +
            ' max-w-0 opacity-0' +
            ' group-hover:max-w-screen-sm group-hover:opacity-100'
          }
          data-testid="badge-description"
        >
          &nbsp;-&nbsp;{description}
        </span>
      )}
    </span>
  );
});
